import { zonedParts, localDateTimeToInstant, validTimeZone } from './shiftTimeZone';
import { getShiftOperationalSummary } from './shiftOperationalSummary';
import { computeShiftActivationUrgency, getShiftStartMs, getShiftEndMs, normalizeShiftRole, shiftValueToMillis } from './shiftLifecycle';
import { buildShiftMetrics } from '../../utils/shiftMetricsEngine';

// Read-only projections of the canonical lifecycle. These keys are UI filters,
// never persisted statuses. Each shift belongs to exactly one visibility group.
export const VISIBILITY_STAGES = [
  ['open', 'Open'], ['requested', 'Requested'], ['awaiting', 'Awaiting Acceptance'],
  ['accepted', 'Accepted / Assigned'], ['activated', 'Activated'],
  ['tracking', 'En Route / Tracking'], ['arrived', 'Arrived'], ['on_shift', 'On Shift'], ['completed', 'Completed'],
];

export function selectDailyShifts(shifts, selectedDayMs, timeZone) {
  const calendar = new Date(selectedDayMs);
  const key = `${calendar.getFullYear()}-${String(calendar.getMonth()+1).padStart(2,'0')}-${String(calendar.getDate()).padStart(2,'0')}`;
  const next = new Date(`${key}T12:00:00Z`); next.setUTCDate(next.getUTCDate()+1);
  const bounds = new Map();
  return shifts.filter(shift => {
    const zone = shift.timeZone || shift.timezone || timeZone;
    if (!validTimeZone(zone)) return false;
    if (!bounds.has(zone)) bounds.set(zone, [
      localDateTimeToInstant(`${key}T00:00`, zone),
      localDateTimeToInstant(`${next.toISOString().slice(0,10)}T00:00`, zone),
    ]);
    const [start,end] = bounds.get(zone);
    const from = getShiftStartMs(shift), to = getShiftEndMs(shift);
    return Number.isFinite(from) && Number.isFinite(to) && from < +end && to > +start;
  });
}

export function getVisibilityStage(shift, summary = getShiftOperationalSummary(shift)) {
  if (summary.displayState === 'on_shift') return 'on_shift';
  if (summary.terminal) return summary.status === 'completed' ? 'completed' : 'closed';
  if (['activated', 'tracking', 'arrived'].includes(summary.status)) return summary.status;
  if (summary.status === 'assigned') return summary.accepted ? 'accepted' : 'awaiting';
  if (summary.status === 'offered') return 'awaiting';
  if (summary.status === 'requested' || (summary.status === 'open' && !summary.assigned && shift.requestedWorkerUids?.length)) return 'requested';
  return 'open';
}

export function getDailyException(shift, summary, now) {
  if (summary.terminal) return summary.status === 'no_show' ? { label: 'No show recorded', tone: 'danger', action: 'Review shift' } : null;
  if (summary.onSite || summary.arrived) return null;
  if (!summary.hasValidTime || summary.status === 'time_required') return { label: 'Shift time needs confirmation', tone: 'warn', action: 'Confirm time' };
  const urgency = computeShiftActivationUrgency(shift, now);
  if (!summary.arrived && ['critical', 'late', 'warning'].includes(urgency)) {
    const label = !summary.confirmedCoverage ? (summary.awaitingAcceptance || summary.status === 'offered' ? 'Worker acceptance pending' : 'Posted position still uncovered')
      : urgency === 'late' ? 'Shift started; worker has not arrived'
      : summary.status === 'assigned' ? 'Accepted; activation pending'
      : summary.status === 'activated' ? 'Activated; En Route pending' : 'En Route; arrival approaching';
    return { label, tone: urgency === 'late' || urgency === 'critical' ? 'danger' : 'warn', action: summary.assigned ? 'Review worker' : 'Review shift' };
  }
  if (getVisibilityStage(shift, summary) === 'requested') return { label: 'Pool request awaiting your review', tone: 'info', action: 'Review request' };
  if (summary.awaitingAcceptance || summary.status === 'offered') return { label: 'Awaiting worker acceptance', tone: 'info', action: 'Review acceptance' };
  return null;
}

export function getScheduleWindow(shift, presets = []) {
  if (shift.isFlexibleTime || shift.flexibleEmergency || shift.coverageType === 'emergency_fill') return { key: 'flex', label: 'Flexible Emergency Fill' };
  const start = new Date(getShiftStartMs(shift)), end = new Date(getShiftEndMs(shift));
  if (!Number.isFinite(+start) || !Number.isFinite(+end)) return { key: 'time_required', label: 'Time required' };
  const timeZone = shift.timeZone || shift.timezone;
  if (!validTimeZone(timeZone)) return {key: 'timezone_required', label: 'Timezone unavailable'};
  const s = zonedParts(start, timeZone), e = zonedParts(end, timeZone);
  const preset = presets.find(p => !p.isFlexibleTime && +s.hour === p.startHour && +e.hour === p.endHour && +s.minute === 0 && +e.minute === 0);
  if (preset) return preset;
  const format = d => d.toLocaleTimeString('en-US', {timeZone, hour:'numeric', minute:'2-digit'});
  const overnight = `${s.year}-${s.month}-${s.day}` !== `${e.year}-${e.month}-${e.day}`;
  return {key: `custom-${s.hour}-${s.minute}-${e.hour}-${e.minute}`, label: `${format(start)}–${format(end)}${overnight ? ' (+1 day)' : ''}`};
}

export function buildDailyBoard({ shifts = [], selectedDayMs, now = Date.now(), roles = [], presets = [], workerIssuesByUid = {}, timeZone }) {
  const daily = selectDailyShifts(shifts, selectedDayMs, timeZone);
  const project = shift => {
    const summary = getShiftOperationalSummary(shift, now);
    let exception = getDailyException(shift, summary, now);
    const issue = !summary.terminal && workerIssuesByUid[summary.assignedWorkerUid];
    if (issue) exception = { label: [exception?.label, issue].filter(Boolean).join(' · '), tone: 'danger', action: 'Review worker' };
    return { shift, summary, stage: getVisibilityStage(shift, summary), role: normalizeShiftRole(shift.role) || 'OTHER', window: getScheduleWindow(shift, presets), exception };
  };
  const rows = daily.map(project);
  // Missing-time records have no trustworthy calendar date. Surface separately
  // instead of silently dropping them or inventing a date/coverage denominator.
  const undated = shifts.filter(shift => {
    const summary = getShiftOperationalSummary(shift, now);
    return !summary.terminal && !summary.hasValidTime;
  }).map(project);
  const metrics = buildShiftMetrics(daily);
  const demand = rows.filter(r => !['cancelled', 'canceled'].includes(r.summary.status));
  const open = demand.filter(r => !r.summary.confirmedCoverage && !r.summary.terminal);
  const alerts = demand.filter(r => r.exception).sort((a, b) => {
    const priority = { danger: 0, warn: 1, info: 2 };
    return priority[a.exception.tone] - priority[b.exception.tone] || getShiftStartMs(a.shift) - getShiftStartMs(b.shift);
  });
  const atRisk = alerts.filter(r => r.exception.tone !== 'info' && !r.summary.terminal);
  const live = demand.filter(r => ['activated', 'tracking', 'arrived'].includes(r.summary.status));
  const activeWorkers = new Set(live.map(r => r.summary.assignedWorkerUid).filter(Boolean)).size;
  const windows = new Map(presets.filter(p => !p.isFlexibleTime).slice(0, 3).map(p => [p.key, p]));
  demand.forEach(r => windows.set(r.window.key, r.window));
  return { timezoneMissing: shifts.filter(shift => !validTimeZone(shift.timeZone || shift.timezone || timeZone)), rows, undated, demand, metrics, open, alerts, atRisk, activeWorkers, live,
    closedDemand: demand.filter(r => r.summary.terminal && !r.summary.confirmedCoverage),
    stages: Object.fromEntries(VISIBILITY_STAGES.map(([key]) => [key, rows.filter(r => r.stage === key)])),
    roles: [...new Set([...roles.map(normalizeShiftRole), ...demand.map(r => r.role)])], windows: [...windows.values()],
  };
}

export function getRowActivity(row) {
  const fields = { requested: ['requestedAt'], awaiting: ['offeredAt', 'assignedAt'], accepted: ['workerAcceptedAt', 'acceptedAt', 'assignmentAcceptedAt'], activated: ['activatedAt'], tracking: ['trackingStartedAt', 'trackingAt', 'onMyWayAt'], arrived: ['arrivedAt'], on_shift: ['arrivedAt'], completed: ['completedAt'] };
  const stageTimes = (fields[row.stage] || ['createdAt']).map(key => shiftValueToMillis(row.shift[key])).filter(Number.isFinite);
  const lastTimes = [...stageTimes, shiftValueToMillis(row.shift.updatedAt)].filter(Number.isFinite);
  return { stageAt: stageTimes.length ? Math.max(...stageTimes) : null, latestAt: lastTimes.length ? Math.max(...lastTimes) : null };
}
